import { Check, Sparkles } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";

export default function Upgrade() {
  const { user } = useAuth();
  const { t } = useLang();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (params.get("cancelled")) toast.info(t("upgrade_cancelled"));
  }, [params, t]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "portfolio_lifetime",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const isPremium = user && user.is_premium;
  const features = [
    t("feature_verified"),
    t("feature_portfolio"),
    t("feature_resolution"),
    t("feature_support"),
  ];

  return (
    <div className="mx-auto max-w-5xl px-6 sm:px-10 py-20" data-testid="upgrade-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-sienna-400 mb-4">
        ● {t("portfolio_tier")}
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 font-light tracking-tight leading-none">
        {t("upgrade_title")}
      </h1>
      <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-zinc-300">
        {t("upgrade_lead")}
      </p>

      <div className="mt-14 grid gap-10 lg:grid-cols-12">
        {/* Pricing card */}
        <div className="lg:col-span-6 border border-zinc-800 p-10 relative">
          <div className="absolute -top-3 left-8 bg-sienna text-white font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1">
            {t("portfolio_tier")}
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-6xl text-zinc-50 font-light">{t("price")}</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
              {t("price_note")}
            </span>
          </div>
          <ul className="mt-10 space-y-4" data-testid="upgrade-feature-list">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-zinc-200 font-light">
                <Check size={16} className="text-sienna-400 shrink-0 mt-1" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          {isPremium ? (
            <div className="mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-sienna-400" data-testid="already-premium-notice">
              ✦ {t("already_premium")}
            </div>
          ) : user ? (
            <button
              onClick={startCheckout}
              disabled={busy}
              className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-sienna text-white py-4 text-sm font-light hover:bg-sienna-400 transition-colors disabled:opacity-60"
              data-testid="upgrade-cta-button"
            >
              <Sparkles size={14} />
              {busy ? t("paying") : t("upgrade_cta")}
            </button>
          ) : (
            <Link
              to="/login"
              className="mt-10 block text-center border border-zinc-800 text-zinc-100 py-4 text-sm font-light hover:bg-zinc-900 hover:border-zinc-700 transition-colors"
              data-testid="upgrade-signin-link"
            >
              {t("sign_in")}
            </Link>
          )}
        </div>

        {/* Side copy */}
        <aside className="lg:col-span-6 font-light text-zinc-300 leading-relaxed space-y-5">
          <p>
            The Portfolio tier exists to support Social Gallery and to reward the photographers
            who take the craft seriously. You pay once. The badge never expires.
          </p>
          <p className="text-zinc-400">
            Secure checkout is handled by Stripe. Social Gallery never sees your card details.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            · No subscriptions ·
          </p>
        </aside>
      </div>
    </div>
  );
}
